import Immutable from 'immutable';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { TOGGLZ_FEATURES } from '@bibliocommons/constants-feature-togglz';
import ProgressBar from '@bibliocommons/shared-progress-bar';

import { trackContentClick, trackContentExplore, trackContentImpression } from 'app/actions/AnalyticsGa4Actions';
import { trackNavLinkClick as createTrackNavLinkClickAction } from 'app/actions/BrowseActions';
import { getRelationships } from 'app/actions/RelationshipsActions';
import { BackToLinkManager } from 'app/components/BackToLinkManager';
import ErrorPage from 'app/components/ErrorPage';
import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import { METADATA_CATEGORY_TYPE, UI_COMPONENT_TYPE, UI_CONTAINER_TYPE } from 'app/constants/AnalyticsGa4Constants';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { mapBrowseConfigElementsToRowConfigs } from 'app/helpers/browse';
import useFeature from 'app/hooks/useFeature';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import { selectBrowseConfig, selectIsFetching } from 'app/selectors/BrowseSelector';
import { selectCatalogBibEntities, selectListEntities, selectUserEntities } from 'app/selectors/EntitiesSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import BrowsePage from './BrowsePage';

const BrowsePageContainer = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const currentLibrary = useSelector(selectCurrentLibrary);
  const currentUser = useSelector(selectCurrentUser);
  const config = useSelector(selectBrowseConfig) ?? Immutable.Map();
  const catalogBibsByMetadataId = useSelector(selectCatalogBibEntities);
  const isFetching = useSelector(selectIsFetching);
  const listsById = useSelector(selectListEntities);
  const usersById = useSelector(selectUserEntities);
  const isCoreV2BrowsePageEnabled = useFeature(TOGGLZ_FEATURES.CORE_V2_BROWSE_PAGE);

  const pageTitle = intl.formatMessage({ defaultMessage: 'Browse', id: 'browse' });

  useEffect(() => {
    if (currentUser?.isEmpty() ?? true) {
      return;
    }

    const listCreatorIds = listsById.valueSeq().map(list => list.get('owner'));

    dispatch(getRelationships({ userId: currentUser.get('id'), userIds: listCreatorIds }));
  }, [currentUser, dispatch, listsById]);

  if (!isCoreV2BrowsePageEnabled) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  const trackBibItemClick = ({ displayBib, itemPosition, rowPosition, rowTitle }) => {
    const { metadataId, title } = displayBib;

    dispatch(
      trackContentClick({
        bibId: metadataId,
        metadata: {
          metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_TITLE,
          metadata_value: title
        },
        ui: {
          ui_component_type: UI_COMPONENT_TYPE.LINK_IMAGE,
          ui_container_position: rowPosition,
          ui_container_title: rowTitle,
          ui_container_type: UI_CONTAINER_TYPE.BROWSE_ROW_BIB,
          ui_content_position: itemPosition
        }
      })
    );
  };

  const trackBibsRowTitleClick = ({ linkText, linkUrl, rowPosition, rowTitle }) => {
    dispatch(
      trackContentExplore({
        metadata: {
          metadata_category_type: METADATA_CATEGORY_TYPE.BROWSE,
          metadata_value: linkUrl
        },
        ui: {
          ui_component_label: linkText,
          ui_component_type: UI_COMPONENT_TYPE.LINK_HEADING,
          ui_container_position: rowPosition,
          ui_container_title: rowTitle,
          ui_container_type: UI_CONTAINER_TYPE.BROWSE_ROW_BIB
        }
      })
    );
  };

  const trackLinksRowLinkClick = ({ itemPosition, linkText, linkUrl, rowPosition, rowTitle }) => {
    dispatch(
      trackContentExplore({
        metadata: {
          metadata_category_type: METADATA_CATEGORY_TYPE.BROWSE,
          metadata_value: linkUrl
        },
        ui: {
          ui_component_label: linkText,
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
          ui_container_position: rowPosition,
          ui_container_title: rowTitle,
          ui_container_type: UI_CONTAINER_TYPE.BROWSE_ROW_LINK,
          ui_content_position: itemPosition
        }
      })
    );
  };

  const trackListCreatorClick = ({ creatorName, itemPosition, list, rowPosition, rowTitle }) => {
    dispatch(
      trackContentExplore({
        list: {
          list_id: list.id,
          list_purpose: (list.listType || '').toLowerCase(),
          list_title: list.name,
          list_visibility: list.visibility
        },
        metadata: {
          metadata_category_type: METADATA_CATEGORY_TYPE.USER,
          metadata_value: creatorName
        },
        ui: {
          ui_component_label: creatorName,
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
          ui_container_position: rowPosition,
          ui_container_title: rowTitle,
          ui_container_type: UI_CONTAINER_TYPE.BROWSE_ROW_LIST,
          ui_content_position: itemPosition
        }
      })
    );
  };

  const trackListImageClick = ({ impressionId, itemPosition, list, rowPosition, rowTitle }) => {
    dispatch(
      trackContentClick({
        list: {
          list_id: list.id,
          list_purpose: (list.listType || '').toLowerCase(),
          list_title: list.name,
          list_visibility: list.visibility
        },
        properties: {
          impression_id: impressionId
        },
        ui: {
          ui_component_type: UI_COMPONENT_TYPE.LINK_IMAGE,
          ui_container_position: rowPosition,
          ui_container_title: rowTitle,
          ui_container_type: UI_CONTAINER_TYPE.BROWSE_ROW_LIST,
          ui_content_position: itemPosition
        }
      })
    );
  };

  const trackListImpression = ({ impressionId, itemPosition, list, rowPosition, rowTitle }) => {
    dispatch(
      trackContentImpression({
        properties: {
          impression_id: impressionId
        },
        list: {
          list_id: list.id,
          list_purpose: (list.listType || '').toLowerCase(),
          list_title: list.name,
          list_visibility: list.visibility
        },
        ui: {
          ui_container_position: rowPosition,
          ui_container_title: rowTitle,
          ui_container_type: UI_CONTAINER_TYPE.BROWSE_ROW_LIST,
          ui_content_position: itemPosition
        }
      })
    );
  };

  const trackListTitleClick = ({ impressionId, itemPosition, list, rowPosition, rowTitle }) => {
    dispatch(
      trackContentClick({
        list: {
          list_id: list.id,
          list_purpose: (list.listType || '').toLowerCase(),
          list_title: list.name,
          list_visibility: list.visibility
        },
        properties: {
          impression_id: impressionId
        },
        ui: {
          ui_component_label: list.name,
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
          ui_container_position: rowPosition,
          ui_container_title: rowTitle,
          ui_container_type: UI_CONTAINER_TYPE.BROWSE_ROW_LIST,
          ui_content_position: itemPosition
        }
      })
    );
  };

  const trackNavLinkClick = ({ itemPosition, linkText, linkUrl, rowPosition, rowTitle }) => {
    dispatch(createTrackNavLinkClickAction({ itemPosition, linkText, linkUrl, rowPosition, rowTitle }));
  };

  const rowConfigs = mapBrowseConfigElementsToRowConfigs(config.get('elements', Immutable.List())?.toJS(), {
    catalogBibsByMetadataId: catalogBibsByMetadataId?.toJS(),
    hasHistoryRoutingForBibTitles: false,
    hasHistoryRoutingForNavLinks: false,
    listsById: listsById?.toJS(),
    trackBibItemClick,
    trackBibsRowTitleClick,
    trackLinksRowLinkClick,
    trackListCreatorClick,
    trackListImageClick,
    trackListImpression,
    trackListTitleClick,
    trackNavLinkClick,
    usersById: usersById?.toJS()
  });

  return (
    <>
      <DocumentHeadContainer
        description={intl.formatMessage(
          { id: 'browse_and_discover_description' },
          { libraryName: currentLibrary?.get('fullName') }
        )}
        pageTitle={pageTitle}
      />
      <ProgressBar active={isFetching} />
      <BackToLinkManager pageId="browse" />
      <BrowsePage pageTitle={pageTitle} rowConfigs={rowConfigs} />
    </>
  );
};

export default BrowsePageContainer;
